import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Calendar, User as UserIcon, MapPin, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Jul 1, 2026 — admin fan-out of the staff Missing-Log "unplanned" bucket.
// Sits alongside "Unplan Visits" (which reads db.non_plan_visits) and shows
// exits detected via post-hoc GPS analysis that the live-track gates
// missed (sparse pings, jitter, etc.). Filters mirror the user's spec:
// date, staff (All | one), geofence (All | one). See
// backend/routes/daily_report.py::daily_unplan_visits for the endpoint.

function fmtClock(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fmtMinutes(m) {
  if (m == null) return '—';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m - h * 60);
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

function todayIso() {
  // IST-based "today" (UTC+5:30) — the whole team is in India and the
  // staff-side Missing-Log tab also thinks in IST. If we used
  // ``new Date().toISOString().slice(0,10)`` here we'd default to UTC
  // today, which at any time before 05:30 IST is YESTERDAY in the
  // admin's mental model — silently hiding real excursions.
  // (Jul 2, 2026 hotfix — reported by user: staff Missing tab shows
  // Unplanned exit but admin Daily-Unplan panel is empty on same date.)
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

export default function DailyUnplanVisits() {
  const [date, setDate] = useState(todayIso());
  const [staffId, setStaffId] = useState('');
  const [geofenceId, setGeofenceId] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [geoOptions, setGeoOptions] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Load the staff + geofence dropdowns once.
  useEffect(() => {
    const token = localStorage.getItem('token');
    // Staff — reuse the existing /api/admin/staff-options endpoint the
    // Daily Report already depends on, so we don't create yet another
    // admin listing endpoint.
    fetch(`${API_URL}/api/admin/staff-options`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((body) => setStaffOptions(body.staff || body || []))
      .catch(() => {});
    // Geofences — /api/geofences returns everything the admin can see.
    fetch(`${API_URL}/api/geofences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((body) => setGeoOptions(Array.isArray(body) ? body : (body.geofences || [])))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams({ date });
      if (staffId) qs.set('staff_id', staffId);
      if (geofenceId) qs.set('geofence_id', geofenceId);
      const r = await fetch(`${API_URL}/api/admin/daily-unplan-visits?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [date, staffId, geofenceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const header = ['Date', 'Staff', 'Geofence', 'From', 'To', 'Duration (min)'];
    const lines = [header.join(',')];
    for (const r of data.rows) {
      lines.push([
        data.date,
        `"${(r.staff_name || '').replace(/"/g, '""')}"`,
        `"${(r.geofence_name || '').replace(/"/g, '""')}"`,
        fmtClock(r.from),
        fmtClock(r.to),
        r.duration_minutes,
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily-unplan-${data.date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="daily-unplan-visits">
      <div className="flex items-center gap-2">
        <AlertTriangle className="text-red-600" size={22} />
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Daily Unplanned Visits</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Every unauthorised geofence exit for the day (matches what each staff sees on their Missing tab). Filter by date, staff, geofence. Sorted by duration (worst first).
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
            <Calendar size={12} /> Date
          </label>
          <input
            type="date"
            value={date}
            max={todayIso()}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-2 py-1.5 border rounded text-sm"
            data-testid="daily-unplan-date"
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
            <UserIcon size={12} /> Staff
          </label>
          <select
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            data-testid="daily-unplan-staff"
          >
            <option value="">All staff</option>
            {staffOptions.map((s) => (
              <option key={s.id || s.staff_id} value={s.id || s.staff_id}>{s.name || s.staff_name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
            <MapPin size={12} /> Geofence
          </label>
          <select
            value={geofenceId}
            onChange={(e) => setGeofenceId(e.target.value)}
            className="w-full px-2 py-1.5 border rounded text-sm bg-white"
            data-testid="daily-unplan-geofence"
          >
            <option value="">All geofences</option>
            {geoOptions.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded text-sm flex items-center gap-1"
          data-testid="daily-unplan-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
        <button
          onClick={exportCsv}
          disabled={!data?.rows?.length}
          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-gray-800 rounded text-sm flex items-center gap-1"
          data-testid="daily-unplan-export"
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      {/* Summary strip */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-xs text-red-700">Unplanned exits</div>
            <div className="text-2xl font-bold text-red-900" data-testid="daily-unplan-count">{data.count}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="text-xs text-orange-700">Total time out</div>
            <div className="text-2xl font-bold text-orange-900">{fmtMinutes(data.total_minutes)}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="text-xs text-gray-600">Date</div>
            <div className="text-2xl font-bold text-gray-900">{data.date}</div>
          </div>
        </div>
      )}

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
          {err}
        </div>
      )}

      {/* Rows */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading && !data ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
        ) : data?.rows?.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Staff</th>
                  <th className="text-left px-3 py-2 font-semibold">Geofence</th>
                  <th className="text-left px-3 py-2 font-semibold">From → To</th>
                  <th className="text-right px-3 py-2 font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((r, i) => (
                  <tr key={`${r.staff_id}-${r.from}-${i}`} data-testid={`daily-unplan-row-${i}`} className="hover:bg-red-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{r.staff_name}</td>
                    <td className="px-3 py-2 text-gray-700">{r.geofence_name}</td>
                    <td className="px-3 py-2 font-mono text-gray-700">{fmtClock(r.from)} → {fmtClock(r.to)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-red-700">{fmtMinutes(r.duration_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-emerald-800 bg-emerald-50 text-sm" data-testid="daily-unplan-empty">
            No unplanned exits for {data?.date || date} with the current filters. 🎉
          </div>
        )}
      </div>
    </div>
  );
}
